// @ts-nocheck
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api, logout } from '../services/api'
import { isPushSupported, getSubscriptionStatus, subscribeToPush, unsubscribeFromPush } from '../services/pushNotifications'
import LangSwitcher from '../components/LangSwitcher'
import { useToast, Toast } from '../components/Toast'

function getInitials(name: string, email: string) {
  if (name) return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
  return email?.[0]?.toUpperCase() || '?'
}

export default function Profile() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [user, setUser] = useState<any>(null)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [notifSubscribed, setNotifSubscribed] = useState(false)
  const [notifDenied, setNotifDenied] = useState(false)
  const { toast, showToast } = useToast(5000)
  const [smsCredits, setSmsCredits] = useState<number | null>(null)

  useEffect(() => {
    const stored = localStorage.getItem('user')
    if (!localStorage.getItem('token')) { navigate('/login'); return }
    if (stored) {
      const u = JSON.parse(stored)
      setUser(u)
      setName(u.name || '')
    }
    if (isPushSupported()) {
      getSubscriptionStatus().then(sub => {
        setNotifSubscribed(sub)
        if (Notification.permission === 'denied') setNotifDenied(true)
      })
    }
    api.get('/sms/credits').then(r => setSmsCredits(r.data.credits)).catch(() => {})
  }, [navigate])

  const handleSaveName = async () => {
    if (!name.trim() || name === user?.name) return
    setSaving(true)
    try {
      await api.patch('/auth/name', { name: name.trim() })
      const updated = { ...user, name: name.trim() }
      setUser(updated)
      localStorage.setItem('user', JSON.stringify(updated))
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {}
    finally { setSaving(false) }
  }

  const handleNotifToggle = async () => {
    if (notifSubscribed) {
      await unsubscribeFromPush()
      setNotifSubscribed(false)
    } else {
      try {
        const ok = await subscribeToPush()
        if (ok) {
          setNotifSubscribed(true)
          setNotifDenied(false)
          showToast('Push notifikácie zapnuté ✓', 'success')
        } else {
          const perm = Notification.permission
          if (perm === 'denied') setNotifDenied(true)
          showToast(`Nepodarilo sa zapnúť (perm: ${perm})`, 'error')
        }
      } catch (err: any) {
        const msg = err?.message || String(err)
        showToast(msg.slice(0, 80), 'error')
        console.error('[push] subscribe error:', err)
        if (Notification.permission === 'denied') setNotifDenied(true)
      }
    }
  }

  const handleLogout = () => { logout(); navigate('/login') }

  if (!user) return null

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', color: '#2D7A3A', fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 0 }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 17, color: '#1a1a1a' }}>{t('profile.title')}</span>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '28px 20px 40px' }}>
        {/* Avatar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28 }}>
          <div style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: '#2D7A3A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: '#fff', marginBottom: 10 }}>
            {getInitials(user.name, user.email)}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>{user.email}</p>
        </div>

        {/* Name */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '18px 20px', marginBottom: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
          <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('profile.name')}
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSaveName()}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 16, color: '#1a1a1a', backgroundColor: 'transparent', fontFamily: 'inherit' }}
              placeholder={t('profile.namePlaceholder')}
            />
            {name !== user?.name && (
              <button
                onClick={handleSaveName}
                disabled={saving}
                style={{ fontSize: 13, fontWeight: 600, color: saved ? '#9ca3af' : '#2D7A3A', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                {saved ? t('profile.saved') : (saving ? '...' : t('profile.save'))}
              </button>
            )}
          </div>
        </div>

        {/* Language */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '18px 20px', marginBottom: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>
            {t('profile.language')}
          </p>
          <LangSwitcher />
        </div>

        {/* Push notifications */}
        {isPushSupported() && (
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: '18px 20px', marginBottom: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>
                {t('profile.pushNotifications')}
              </p>
              {notifDenied && (
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#f59e0b' }}>
                {t('profile.pushBlocked')}
              </p>
            )}
            </div>
            <button
              onClick={handleNotifToggle}
              disabled={notifDenied}
              style={{
                width: 48, height: 28, borderRadius: 14, border: 'none', cursor: notifDenied ? 'not-allowed' : 'pointer', padding: 3,
                backgroundColor: notifSubscribed ? '#2D7A3A' : '#e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: notifSubscribed ? 'flex-end' : 'flex-start',
                transition: 'background 0.2s', opacity: notifDenied ? 0.5 : 1
              }}
            >
              <div style={{ width: 22, height: 22, borderRadius: 11, backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
            </button>
          </div>
        )}

        {/* Links */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, overflow: 'hidden', marginBottom: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
          {[
            { label: t('profile.notificationHistory'), path: '/notifications' },
            { label: t('profile.smsCredits'), path: '/sms-credits', badge: smsCredits },
            { label: t('profile.support'), path: '/support' },
            { label: t('profile.whatsNew'), path: '/changelog' },
            { label: t('profile.accountSettings'), path: '/settings' },
          ].map((item, i, arr) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              style={{ width: '100%', padding: '16px 20px', border: 'none', background: 'none', textAlign: 'left', fontSize: 15, color: '#1a1a1a', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: i < arr.length - 1 ? '1px solid #f5f5f5' : 'none', fontFamily: 'inherit' }}
            >
              {item.label}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {(item as any).badge !== undefined && (item as any).badge !== null && (
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                    backgroundColor: (item as any).badge > 10 ? '#f0fdf4' : (item as any).badge > 3 ? '#fff7ed' : '#fef2f2',
                    color: (item as any).badge > 10 ? '#16a34a' : (item as any).badge > 3 ? '#ea580c' : '#dc2626',
                  }}>
                    {(item as any).badge} SMS
                  </span>
                )}
                <span style={{ color: '#d1d5db', fontSize: 18 }}>›</span>
              </div>
            </button>
          ))}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          style={{ width: '100%', padding: '15px', borderRadius: 16, border: '1.5px solid #fee2e2', backgroundColor: '#fff', color: '#ef4444', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          {t('profile.logout')}
        </button>
      </div>
      <Toast toast={toast} />
    </div>
  )
}
